import React, { useState, useEffect } from 'react';
import { Search, Filter, X, Calendar, User, DollarSign, Award, Store, Download, FileSpreadsheet, FileText, Clock } from 'lucide-react';
import Modal from './Modal';
import {
  buildDefaultClientSearchFilters,
  buildResetClientSearchFilters,
  shouldSkipClientSideAdvancedFilters,
} from '../utils/clientSearchFilters';

const ClientSearch = ({
  clients,
  onFilteredClients,
  onSearch,
  searchTerm: controlledSearchTerm,
  commercants = [],
  totalCount = null,
  skipTextSearch = false,
  pageRangeLabel = null,
  onAdvancedFiltersChange = null,
}) => {
  // Récupérer l'utilisateur actuel pour détecter si c'est un commerçant
  const getCurrentUser = () => {
    try {
      const userData = localStorage.getItem('admin_user');
      if (userData) {
        return JSON.parse(userData);
      }
    } catch (error) {
      console.error('Erreur parsing user data:', error);
    }
    return null;
  };

  const currentUser = getCurrentUser();
  const normalizedRole =
    currentUser?.role === 'super-admin' || currentUser?.role === 'SUPER_ADMIN'
      ? 'SUPER_ADMIN'
      : currentUser?.role === 'commercant' || currentUser?.role === 'COMMERCANT'
        ? 'COMMERCANT'
        : currentUser?.role;
  const isCommercant = normalizedRole === 'COMMERCANT';
  const isSuperAdmin = normalizedRole === 'SUPER_ADMIN';
  const userCommercantId = currentUser?.commercantId || currentUser?.commercant?.id || null;
  const isSearchControlled = controlledSearchTerm !== undefined;
  const [uncontrolledSearchTerm, setUncontrolledSearchTerm] = useState('');
  const searchTerm = isSearchControlled ? controlledSearchTerm : uncontrolledSearchTerm;
  const setSearchTermValue = (value) => {
    if (isSearchControlled) {
      if (typeof onSearch === 'function') onSearch(value);
    } else {
      setUncontrolledSearchTerm(value);
    }
  };
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  // SA : commercants [] par défaut (jamais les 3 cochés). Commerçant : sa boutique seule.
  const [filters, setFilters] = useState(() =>
    buildDefaultClientSearchFilters({ isCommercant, userCommercantId })
  );
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportOptions, setExportOptions] = useState({
    format: 'excel',
    includePersonalInfo: true,
    includeContact: true,
    includeLoyalty: true,
    includeOrders: true,
    includeActivity: true,
    scope: 'filtered'
  });

  // Périodes disponibles
  const periods = [
    { value: '7j', label: '7 derniers jours' },
    { value: '30j', label: '30 derniers jours' },
    { value: '3m', label: '3 derniers mois' },
    { value: '6m', label: '6 derniers mois' },
    { value: '1an', label: '1 an' },
    { value: 'all', label: 'Toutes les périodes' }
  ];

  // Niveaux de fidélité
  const loyaltyLevels = [
    { value: 'all', label: 'Tous les niveaux' },
    { value: 'BRONZE', label: 'Bronze' },
    { value: 'SILVER', label: 'Argent' },
    { value: 'GOLD', label: 'Or' },
    { value: 'PLATINUM', label: 'Platine' }
  ];

  // Tranches de montant
  const amountRanges = [
    { value: 'all', label: 'Tous les montants' },
    { value: '0-50', label: '0€ - 50€' },
    { value: '50-100', label: '50€ - 100€' },
    { value: '100-500', label: '100€ - 500€' },
    { value: '500+', label: '500€ et plus' }
  ];

  // Tranches de panier moyen
  const averageBaskets = [
    { value: 'all', label: 'Tous les paniers' },
    { value: '0-25', label: '0€ - 25€' },
    { value: '25-50', label: '25€ - 50€' },
    { value: '50-100', label: '50€ - 100€' },
    { value: '100+', label: '100€ et plus' }
  ];

  // Périodes d'activité
  const activityPeriods = [
    { value: 'all', label: 'Toute période' },
    { value: '7j', label: 'Actif dernière semaine' },
    { value: '30j', label: 'Actif dernier mois' },
    { value: '3m', label: 'Actif 3 derniers mois' },
    { value: '6m', label: 'Actif 6 derniers mois' },
    { value: '1an', label: 'Actif dernière année' }
  ];

  // Options de tri
  const sortOptions = [
    { value: 'dateCreation', label: 'Date d\'inscription' },
    { value: 'totalSpent', label: 'Montant dépensé' },
    { value: 'loyaltyPoints', label: 'Points fidélité' },
    { value: 'orderCount', label: 'Nombre de commandes' },
    { value: 'lastOrder', label: 'Dernière commande' },
    { value: 'averageBasket', label: 'Panier moyen' }
  ];

  // Fonction pour obtenir le niveau de fidélité basé sur les points
  const getLoyaltyLevel = (points) => {
    if (points >= 1000) return 'PLATINUM';
    if (points >= 500) return 'GOLD';
    if (points >= 100) return 'SILVER';
    return 'BRONZE';
  };

  // Fonction pour calculer le montant total dépensé
  const getTotalSpent = (client) => {
    if (!client || !client.commandes) return 0;
    return client.commandes.reduce((total, commande) => {
      if (commande && commande.statut !== 'ANNULEE' && commande.produits) {
        return total + commande.produits.reduce((sum, item) => {
          return sum + (item.quantite * item.prixUnitaire);
        }, 0);
      }
      return total;
    }, 0);
  };

  // Fonction pour calculer le panier moyen
  const getAverageBasket = (client) => {
    if (!client || !client.commandes) return 0;
    const validOrders = client.commandes.filter(c => c && c.statut !== 'ANNULEE') || [];
    if (validOrders.length === 0) return 0;
    
    const totalSpent = getTotalSpent(client);
    return totalSpent / validOrders.length;
  };

  // Fonction pour vérifier si un client est actif dans une période
  const isActiveInPeriod = (client, period) => {
    if (period === 'all') return true;
    if (!client || !client.commandes) return false;
    
    const lastOrder = client.commandes
      .filter(c => c && c.statut !== 'ANNULEE' && c.dateCommande)
      .sort((a, b) => new Date(b.dateCommande) - new Date(a.dateCommande))[0];
    
    if (!lastOrder) return false;
    
    const lastOrderDate = new Date(lastOrder.dateCommande);
    const now = new Date();
    
    switch (period) {
      case '7j':
        return lastOrderDate > new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      case '30j':
        return lastOrderDate > new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      case '3m':
        return lastOrderDate > new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      case '6m':
        return lastOrderDate > new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000);
      case '1an':
        return lastOrderDate > new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
      default:
        return true;
    }
  };

  // Fonction pour vérifier si un client a commandé chez un commerçant
  const hasOrderedFromMerchant = (client, merchantId) => {
    return client.commandes?.some(commande => 
      commande.commercantId === merchantId && commande.statut !== 'ANNULEE'
    ) || false;
  };

  // Fonction pour vérifier si un client correspond à la tranche de panier moyen
  const isInAverageBasketRange = (client, range) => {
    if (range === 'all') return true;
    
    const averageBasket = getAverageBasket(client);
    
    switch (range) {
      case '0-25':
        return averageBasket >= 0 && averageBasket <= 25;
      case '25-50':
        return averageBasket > 25 && averageBasket <= 50;
      case '50-100':
        return averageBasket > 50 && averageBasket <= 100;
      case '100+':
        return averageBasket > 100;
      default:
        return true;
    }
  };

  // Fonction pour vérifier si un client correspond à la période
  const isInPeriod = (client, period) => {
    if (period === 'all') return true;
    if (!client || !client.dateCreation) return false;
    
    const clientDate = new Date(client.dateCreation);
    const now = new Date();
    
    switch (period) {
      case '7j':
        return clientDate > new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      case '30j':
        return clientDate > new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      case '3m':
        return clientDate > new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      case '6m':
        return clientDate > new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000);
      case '1an':
        return clientDate > new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
      default:
        return true;
    }
  };

  // Fonction pour vérifier si un client correspond à la tranche de montant
  const isInAmountRange = (client, range) => {
    if (range === 'all') return true;
    
    const totalSpent = getTotalSpent(client);
    
    switch (range) {
      case '0-50':
        return totalSpent >= 0 && totalSpent <= 50;
      case '50-100':
        return totalSpent > 50 && totalSpent <= 100;
      case '100-500':
        return totalSpent > 100 && totalSpent <= 500;
      case '500+':
        return totalSpent > 500;
      default:
        return true;
    }
  };

  // Fonction de filtrage et tri
  const filterAndSortClients = () => {
    // SA mode serveur (skipTextSearch) : page partielle — ne pas filtrer
    // merchant/loyalty/amount/activity côté client (sinon total/affichage faux).
    // Recherche texte = serveur. Statut principal = onglets SA / API.
    if (shouldSkipClientSideAdvancedFilters(skipTextSearch)) {
      return (clients || []).filter((client) => client && client.email);
    }

    // Pour un commerçant, appliquer seulement la recherche textuelle et les filtres de base
    // Car l'API backend a déjà retourné les bons clients
    let filtered = clients.filter(client => {
      // Vérifier que le client existe et a les propriétés nécessaires
      if (!client || !client.email) return false;
      
      // Recherche textuelle (nom, prénom, email, téléphone, numéro de carte)
      const searchLower = searchTerm.toLowerCase();
      const searchDigits = String(searchTerm).replace(/\D/g, '');
      const phoneDigits = client.telephone ? String(client.telephone).replace(/\D/g, '') : '';
      const matchesPhone = Boolean(
        client.telephone && (
          client.telephone.toLowerCase().includes(searchLower) ||
          (searchDigits.length > 0 && phoneDigits.includes(searchDigits))
        )
      );
      const matchesSearch = skipTextSearch || !searchTerm ||
        (client.nom && client.nom.toLowerCase().includes(searchLower)) ||
        (client.prenom && client.prenom.toLowerCase().includes(searchLower)) ||
        client.email.toLowerCase().includes(searchLower) ||
        matchesPhone ||
        (client.numeroCarte && client.numeroCarte.toLowerCase().includes(searchLower));

      // Pour un commerçant, filtrer uniquement par recherche et statut de base
      if (isCommercant) {
        const matchesStatus = filters.status.length === 0 || 
          filters.status.includes(client.statut || 'ACTIF');
        return matchesSearch && matchesStatus;
      }

      // Pour super admin (mode non-serveur / liste complète), appliquer les filtres
      // Filtre période
      const matchesPeriod = isInPeriod(client, filters.period);

      // Filtre niveau de fidélité
      const clientLoyaltyLevel = getLoyaltyLevel(client.pointsClickorico?.points || 0);
      const matchesLoyaltyLevel = filters.loyaltyLevel === 'all' || 
        clientLoyaltyLevel === filters.loyaltyLevel;

      // Filtre tranche de montant
      const matchesAmountRange = isInAmountRange(client, filters.amountRange);

      // Filtre statut
      const clientStatus = client.statut || 'ACTIF';
      const matchesStatus = filters.status.length === 0 || 
        filters.status.includes(clientStatus);
      
      if (!matchesStatus) {
        console.log('[ClientSearch] Client exclu par statut:', client.email, 'statut:', clientStatus, 'filtres:', filters.status);
      }

      // Filtre commerçants — uniquement si l'utilisateur a coché au moins un commerçant
      // (défaut SA = [] → pas de filtre). Jamais cocher tous par défaut.
      const matchesMerchants = filters.commercants.length === 0 || 
        filters.commercants.some(merchantId => hasOrderedFromMerchant(client, merchantId));

      // Filtre période d'activité
      const matchesActivityPeriod = isActiveInPeriod(client, filters.activityPeriod);

      // Filtre panier moyen
      const matchesAverageBasket = isInAverageBasketRange(client, filters.averageBasket);

      return matchesSearch && matchesPeriod && matchesLoyaltyLevel && 
             matchesAmountRange && matchesStatus && matchesMerchants && 
             matchesActivityPeriod && matchesAverageBasket;
    });

    // Tri
    filtered.sort((a, b) => {
      if (!a || !b) return 0;
      let aValue, bValue;

      switch (filters.sortBy) {
        case 'dateCreation':
          aValue = a.dateCreation ? new Date(a.dateCreation) : new Date(0);
          bValue = b.dateCreation ? new Date(b.dateCreation) : new Date(0);
          break;
        case 'totalSpent':
          aValue = getTotalSpent(a);
          bValue = getTotalSpent(b);
          break;
        case 'loyaltyPoints':
          aValue = a.pointsClickorico?.points || 0;
          bValue = b.pointsClickorico?.points || 0;
          break;
        case 'orderCount':
          aValue = a.commandes?.length || 0;
          bValue = b.commandes?.length || 0;
          break;
        case 'lastOrder':
          const aLastOrder = a.commandes?.sort((x, y) => new Date(y.dateCommande) - new Date(x.dateCommande))[0];
          const bLastOrder = b.commandes?.sort((x, y) => new Date(y.dateCommande) - new Date(x.dateCommande))[0];
          aValue = aLastOrder ? new Date(aLastOrder.dateCommande) : new Date(0);
          bValue = bLastOrder ? new Date(bLastOrder.dateCommande) : new Date(0);
          break;
        case 'averageBasket':
          aValue = getAverageBasket(a);
          bValue = getAverageBasket(b);
          break;
        default:
          return 0;
      }

      if (filters.sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  };

  // Mise à jour des clients filtrés
  useEffect(() => {
    console.log('[ClientSearch] Filtrage des clients:', {
      clientsCount: clients?.length || 0,
      searchTerm,
      filters,
      firstClient: clients?.[0]
    });
    const filtered = filterAndSortClients();
    console.log('[ClientSearch] Clients filtrés:', filtered.length);
    onFilteredClients(filtered);
    // Controlled: parent owns searchTerm via onChange — do not echo back (avoids reset loops).
    if (!isSearchControlled && typeof onSearch === 'function') {
      onSearch(searchTerm);
    }
  }, [searchTerm, filters, clients]);

  const notifyAdvancedFiltersChange = () => {
    if (typeof onAdvancedFiltersChange === 'function') {
      onAdvancedFiltersChange();
    }
  };

  const updateAdvancedFilter = (updater) => {
    notifyAdvancedFiltersChange();
    setFilters(updater);
  };

  // Gestion des filtres de statut
  const handleStatusChange = (status, checked) => {
    updateAdvancedFilter((prev) =>
      checked
        ? { ...prev, status: [...prev.status, status] }
        : { ...prev, status: prev.status.filter((s) => s !== status) }
    );
  };

  // Gestion des filtres de commerçants
  const handleMerchantChange = (merchantId, checked) => {
    updateAdvancedFilter((prev) =>
      checked
        ? { ...prev, commercants: [...prev.commercants, merchantId] }
        : { ...prev, commercants: prev.commercants.filter((id) => id !== merchantId) }
    );
  };

  // Fonction d'export
  const handleExport = () => {
    const clientsToExport = exportOptions.scope === 'filtered' ? filteredClients : clients;
    
    if (exportOptions.format === 'csv') {
      exportToCSV(clientsToExport);
    } else {
      exportToExcel(clientsToExport);
    }
    
    setShowExportModal(false);
  };

  // Export CSV
  const exportToCSV = (clients) => {
    const headers = [];
    if (exportOptions.includePersonalInfo) headers.push('Nom', 'Prénom', 'Email');
    if (exportOptions.includeContact) headers.push('Téléphone', 'Ville');
    if (exportOptions.includeLoyalty) headers.push('Points fidélité', 'Niveau');
    if (exportOptions.includeOrders) headers.push('Total dépensé', 'Nombre commandes', 'Panier moyen');
    if (exportOptions.includeActivity) headers.push('Date inscription', 'Dernière commande');

    const csvContent = [
      headers.join(','),
      ...clients.filter(client => client && client.email).map(client => {
        const row = [];
        if (exportOptions.includePersonalInfo) {
          row.push(`"${client.nom || ''}"`, `"${client.prenom || ''}"`, `"${client.email || ''}"`);
        }
        if (exportOptions.includeContact) {
          row.push(`"${client.telephone || ''}"`, `"${client.ville || ''}"`);
        }
        if (exportOptions.includeLoyalty) {
          row.push(client.pointsClickorico?.points || 0, getLoyaltyLevel(client.pointsClickorico?.points || 0));
        }
        if (exportOptions.includeOrders) {
          row.push(getTotalSpent(client), client.commandes?.length || 0, getAverageBasket(client).toFixed(2));
        }
        if (exportOptions.includeActivity) {
          row.push(client.dateCreation, client.commandes?.[0]?.dateCommande || '');
        }
        return row.join(',');
      })
    ].join('\n');

    downloadFile(csvContent, 'clients.csv', 'text/csv');
  };

  // Export Excel (simulation avec CSV amélioré)
  const exportToExcel = (clients) => {
    // Pour l'instant, on utilise CSV avec un nom .xlsx
    // Dans un vrai projet, on utiliserait une librairie comme xlsx
    exportToCSV(clients);
  };

  // Fonction de téléchargement
  const downloadFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Réinitialiser les filtres (commercants toujours [] — jamais les 3 cochés)
  const resetFilters = () => {
    notifyAdvancedFiltersChange();
    setFilters(buildResetClientSearchFilters());
    setSearchTermValue('');
  };

  const filteredClients = filterAndSortClients();

  return (
    <div className="space-y-4">
      {/* Barre de recherche principale */}
      <div className="card">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              name="clients-list-search"
              role="searchbox"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              placeholder="Rechercher un client (nom, prénom, email, téléphone, numéro de carte)..."
              value={searchTerm}
              onChange={(e) => setSearchTermValue(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center space-x-2 ${
              showAdvancedFilters 
                ? 'bg-blue-100 text-blue-700' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Filter className="h-4 w-4" />
            <span>Filtres avancés</span>
          </button>
          <button
            onClick={() => setShowExportModal(true)}
            className="px-4 py-2 rounded-lg text-sm font-medium flex items-center space-x-2 bg-green-100 text-green-700 hover:bg-green-200"
          >
            <Download className="h-4 w-4" />
            <span>Exporter</span>
          </button>
        </div>
      </div>

      {/* Filtres avancés */}
      {showAdvancedFilters && (
        <div className="card">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Filtres avancés</h3>
              <button
                onClick={resetFilters}
                className="text-sm text-gray-500 hover:text-gray-700 flex items-center space-x-1"
              >
                <X className="h-4 w-4" />
                <span>Réinitialiser</span>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {/* Période */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="h-4 w-4 inline mr-1" />
                  Période
                </label>
                <select
                  value={filters.period}
                  onChange={(e) => updateAdvancedFilter((prev) => ({ ...prev, period: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {periods.map(period => (
                    <option key={period.value} value={period.value}>
                      {period.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Niveau de fidélité */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Award className="h-4 w-4 inline mr-1" />
                  Niveau fidélité
                </label>
                <select
                  value={filters.loyaltyLevel}
                  onChange={(e) => updateAdvancedFilter((prev) => ({ ...prev, loyaltyLevel: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {loyaltyLevels.map(level => (
                    <option key={level.value} value={level.value}>
                      {level.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Tranche de montant */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <DollarSign className="h-4 w-4 inline mr-1" />
                  Montant dépensé
                </label>
                <select
                  value={filters.amountRange}
                  onChange={(e) => updateAdvancedFilter((prev) => ({ ...prev, amountRange: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {amountRanges.map(range => (
                    <option key={range.value} value={range.value}>
                      {range.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Tri */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <User className="h-4 w-4 inline mr-1" />
                  Trier par
                </label>
                <div className="flex space-x-2">
                  <select
                    value={filters.sortBy}
                    onChange={(e) => updateAdvancedFilter((prev) => ({ ...prev, sortBy: e.target.value }))}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {sortOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => updateAdvancedFilter((prev) => ({
                      ...prev,
                      sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc'
                    }))}
                    className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    {filters.sortOrder === 'asc' ? '↑' : '↓'}
                  </button>
                </div>
              </div>

              {/* Commerçants — SA skipTextSearch : checkboxes visibles mais filtre client-side ignoré */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Store className="h-4 w-4 inline mr-1" />
                  Commerçants
                </label>
                {skipTextSearch && isSuperAdmin && (
                  <p className="text-xs text-amber-700 mb-2">
                    Filtre boutique non appliqué en mode pagination serveur (évite de réduire la page).
                  </p>
                )}
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {commercants.map(merchant => {
                    const isUserCommercant = isCommercant && merchant.id === userCommercantId;
                    // Défaut SA = aucun coché. checked strictement via includes (pas « tous si length match »).
                    const isChecked = filters.commercants.includes(merchant.id);
                    return (
                      <label 
                        key={merchant.id} 
                        className={`flex items-center space-x-2 text-sm ${isUserCommercant ? 'opacity-75' : ''}`}
                        title={isUserCommercant ? 'Votre boutique (non modifiable)' : ''}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => handleMerchantChange(merchant.id, e.target.checked)}
                          disabled={isUserCommercant || (skipTextSearch && isSuperAdmin)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                        <span className={`text-gray-700 ${isUserCommercant ? 'font-semibold' : ''}`}>
                          {merchant.nom}
                          {isUserCommercant && ' (Votre boutique)'}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Période d'activité */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Clock className="h-4 w-4 inline mr-1" />
                  Dernière activité
                </label>
                <select
                  value={filters.activityPeriod}
                  onChange={(e) => updateAdvancedFilter((prev) => ({ ...prev, activityPeriod: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {activityPeriods.map(period => (
                    <option key={period.value} value={period.value}>
                      {period.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Panier moyen */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <DollarSign className="h-4 w-4 inline mr-1" />
                  Panier moyen
                </label>
                <select
                  value={filters.averageBasket}
                  onChange={(e) => updateAdvancedFilter((prev) => ({ ...prev, averageBasket: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {averageBaskets.map(basket => (
                    <option key={basket.value} value={basket.value}>
                      {basket.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Statuts */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Statut
              </label>
              <div className="flex flex-wrap gap-2">
                {['ACTIF', 'INACTIF', 'SUSPENDU'].map(status => (
                  <label key={status} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={filters.status.includes(status)}
                      onChange={(e) => handleStatusChange(status, e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{status}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Résumé des résultats */}
      <div className="flex items-center justify-between text-sm text-gray-600">
        <span>
          {pageRangeLabel
            ? `📊 ${pageRangeLabel}`
            : typeof totalCount === 'number'
              ? `📊 ${totalCount} client${totalCount > 1 ? 's' : ''} au total`
              : `📊 ${filteredClients.length} client${filteredClients.length > 1 ? 's' : ''} trouvé${filteredClients.length > 1 ? 's' : ''}`}
          {searchTerm && !pageRangeLabel ? ` pour "${searchTerm}"` : ''}
          {searchTerm && pageRangeLabel ? ` — recherche « ${searchTerm} »` : ''}
        </span>
        {showAdvancedFilters && (
          <span className="text-xs">
            Filtres actifs: {filters.period} • {filters.loyaltyLevel} • {filters.amountRange}
            {filters.commercants.length > 0 && ` • ${filters.commercants.length} commerçant(s)`}
            {filters.activityPeriod !== 'all' && ` • Actif: ${filters.activityPeriod}`}
          </span>
        )}
      </div>

      {/* Modal d'export */}
      <Modal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        title="📊 Exporter les clients"
        maxWidth="max-w-md"
      >
        <div className="space-y-4">
              {/* Format */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Format
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="excel"
                      checked={exportOptions.format === 'excel'}
                      onChange={(e) => setExportOptions(prev => ({ ...prev, format: e.target.value }))}
                      className="mr-2"
                    />
                    <FileSpreadsheet className="h-4 w-4 mr-1" />
                    Excel
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="csv"
                      checked={exportOptions.format === 'csv'}
                      onChange={(e) => setExportOptions(prev => ({ ...prev, format: e.target.value }))}
                      className="mr-2"
                    />
                    <FileText className="h-4 w-4 mr-1" />
                    CSV
                  </label>
                </div>
              </div>

              {/* Scope */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Périphérie
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="filtered"
                      checked={exportOptions.scope === 'filtered'}
                      onChange={(e) => setExportOptions(prev => ({ ...prev, scope: e.target.value }))}
                      className="mr-2"
                    />
                    Clients filtrés ({filteredClients.length})
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="all"
                      checked={exportOptions.scope === 'all'}
                      onChange={(e) => setExportOptions(prev => ({ ...prev, scope: e.target.value }))}
                      className="mr-2"
                    />
                    Tous les clients ({clients.length})
                  </label>
                </div>
              </div>

              {/* Données à inclure */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Données à inclure
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={exportOptions.includePersonalInfo}
                      onChange={(e) => setExportOptions(prev => ({ ...prev, includePersonalInfo: e.target.checked }))}
                      className="mr-2"
                    />
                    Informations personnelles (nom, prénom, email)
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={exportOptions.includeContact}
                      onChange={(e) => setExportOptions(prev => ({ ...prev, includeContact: e.target.checked }))}
                      className="mr-2"
                    />
                    Contact (téléphone, ville)
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={exportOptions.includeLoyalty}
                      onChange={(e) => setExportOptions(prev => ({ ...prev, includeLoyalty: e.target.checked }))}
                      className="mr-2"
                    />
                    Fidélité (points, niveau)
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={exportOptions.includeOrders}
                      onChange={(e) => setExportOptions(prev => ({ ...prev, includeOrders: e.target.checked }))}
                      className="mr-2"
                    />
                    Commandes (total, nombre, panier moyen)
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={exportOptions.includeActivity}
                      onChange={(e) => setExportOptions(prev => ({ ...prev, includeActivity: e.target.checked }))}
                      className="mr-2"
                    />
                    Activité (inscription, dernière commande)
                  </label>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowExportModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Annuler
              </button>
              <button
                onClick={handleExport}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2"
              >
                <Download className="h-4 w-4" />
                <span>Exporter</span>
              </button>
            </div>
      </Modal>
    </div>
  );
};

export default ClientSearch;
